function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef, useState } from 'react';
import { useInputState } from 'theming/hooks/useInputState';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { useFormContext } from 'components/Form/FormContext';
import { determineComponentState } from 'components/InputBase/InputBase.utils';
import { InputCharacterCounter } from 'components/InputCharacterCounter/InputCharacterCounter';
import * as S from 'components/InputControl/InputControl.styles';
import { InputErrorMessage } from 'components/InputErrorMessage/InputErrorMessage';
import { InputLabel } from 'components/InputLabel/InputLabel';
import { TextAreaBase } from 'components/TextAreaBase/TextAreaBase';
import { Tooltip } from 'components/Tooltip/Tooltip';
import * as T from './TextArea.styles';
const TextArea = /*#__PURE__*/React.forwardRef(({
  ariaLabel,
  label,
  id,
  disabled,
  required,
  optional,
  readOnly,
  placeholder,
  hasError,
  errorMessage,
  maxLength,
  value,
  onChange,
  showCharacterCounter = true,
  width = '100%',
  size,
  resize = true,
  orientation,
  tooltip,
  tooltipProps,
  ...baseProps
}, ref) => {
  if (required && optional) {
    console.warn('An input can be required or optional but not both.');
  }
  const [internalValue, setInternalValue] = useState(value ?? '');
  const internalRef = useRef(null);
  const {
    isFocused,
    isHovered
  } = useInputState(internalRef);
  const context = useFormContext();
  const handleChange = e => {
    if (onChange) {
      onChange(e);
    }
    setInternalValue(e.target.value);
  };
  const inputState = determineComponentState({
    error: hasError,
    disabled,
    readOnly,
    isFocused,
    isHovered
  });
  const isInteractive = !(disabled || readOnly);
  const hasErrorMessage = inputState === 'error' && errorMessage;
  const hasCharacterCounter = showCharacterCounter && maxLength;
  const showFooter = hasErrorMessage || hasCharacterCounter;
  const handleContainerClick = () => {
    if (isInteractive) {
      internalRef.current?.focus();
    }
  };
  const inputProps = {
    id,
    ariaLabel,
    disabled,
    readOnly,
    required,
    placeholder,
    ...baseProps
  };
  const assignSharedRefs = createAssignSharedRefs(ref, internalRef);
  const getSize = () => {
    if (size) {
      return size;
    }
    if (context?.inputSize) {
      return context.inputSize;
    }
    if (context?.orientation === 'horizontal') {
      return 'medium';
    }
    return 'large';
  };
  const getIsHorizontal = () => {
    if (orientation === 'vertical') {
      return false;
    }
    return orientation === 'horizontal' || context?.orientation === 'horizontal';
  };
  const isHorizontal = getIsHorizontal();
  return /*#__PURE__*/React.createElement(S.InputControlWrapper, {
    onClick: handleContainerClick,
    $isHorizontal: isHorizontal,
    $isInteractive: isInteractive
  }, /*#__PURE__*/React.createElement(S.InputHeaderContainer, {
    $isHorizontal: isHorizontal
  }, /*#__PURE__*/React.createElement(T.TextAreaHorizontalContainer, {
    $isHorizontal: isHorizontal
  }, /*#__PURE__*/React.createElement(S.InputHeaderLeftContainer, {
    $isHorizontal: isHorizontal
  }, !!label ? /*#__PURE__*/React.createElement(InputLabel, {
    htmlFor: id,
    inputState: inputState,
    optional: optional,
    required: required,
    orientation: orientation ?? context?.orientation
  }, label) : null, !!tooltip ? /*#__PURE__*/React.createElement(Tooltip, _extends({
    content: tooltip
  }, tooltipProps)) : null))), /*#__PURE__*/React.createElement(T.TextAreaContainer, null, /*#__PURE__*/React.createElement(TextAreaBase, _extends({
    ref: assignSharedRefs,
    inputState: inputState,
    size: getSize(),
    value: internalValue,
    onChange: handleChange,
    maxLength: maxLength
  }, inputProps))), showFooter ? /*#__PURE__*/React.createElement(S.InputFooterContainer, null, hasErrorMessage ? /*#__PURE__*/React.createElement(InputErrorMessage, {
    orientation: orientation ?? context?.orientation
  }, errorMessage) : null, hasCharacterCounter ? /*#__PURE__*/React.createElement(T.CharacterCounterContainer, null, /*#__PURE__*/React.createElement(InputCharacterCounter, {
    currentLength: internalValue.toString().length,
    inputState: inputState,
    maxLength: maxLength
  })) : null) : null);
});
export { TextArea };